import {
  createContext,
  FunctionComponent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  CombinedRunStepForTree,
  CombinedStepForTree,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';

// shell type which allows conditional/component branch nodes in the
// flow tree to be panned to via deep link or on client validation error
export type BranchNode = {
  type: 'branch';
  token: string;
  isConnector?: boolean;
};

export type TreeReferenceStep = CombinedStepForTree | CombinedRunStepForTree | BranchNode;
type TreeReferences = {
  ref: HTMLDivElement;
  step: TreeReferenceStep;
  id: string;
};

interface InquiryTemplateTreeStore {
  screenAndNodeRefs: TreeReferences[];
  setActiveNodeToken?: (token: string) => void;
  saveRef: (ref: HTMLDivElement, step: TreeReferenceStep, id: string) => void;
}

const InquiryTemplateTreeContext = createContext<InquiryTemplateTreeStore | undefined>(undefined);

export const InquiryTemplateTreeProvider: FunctionComponent<{
  children: ReactNode;
  value: InquiryTemplateTreeStore;
}> = ({ children, value }) => {
  return (
    <InquiryTemplateTreeContext.Provider value={value}>
      {children}
    </InquiryTemplateTreeContext.Provider>
  );
};

type UseInquiryTemplateFlowEditorStoreProps = {
  tree: CombinedStepForTree[] | CombinedRunStepForTree[];
  setActiveNodeToken?: (token: string) => void;
};

export const useInquiryTemplateTreeStore = ({
  tree,
  setActiveNodeToken,
}: UseInquiryTemplateFlowEditorStoreProps): InquiryTemplateTreeStore => {
  const [refs, setRefs] = useState<TreeReferences[]>([]);

  useEffect(() => {
    setRefs([]);
  }, [tree]);

  const saveRef = useCallback(
    (ref: HTMLDivElement, step: TreeReferenceStep, id: string) => {
      const savedScreen = refs.some(({ ref: screenRef }) => screenRef === ref);
      if (!savedScreen && ref != null) {
        setRefs((refs) => refs.concat({ ref, step, id }));
      }
    },
    [refs],
  );

  return useMemo(
    () => ({
      screenAndNodeRefs: refs,
      setActiveNodeToken,
      saveRef,
    }),
    [refs, setActiveNodeToken, saveRef],
  );
};

export const useInquiryTemplateTreeContent = () => useContext(InquiryTemplateTreeContext);
